import { useState, useRef } from 'react';
import type { OverlayCreate } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { Modal } from '@/components/ui/Modal';
import { Z_INDEX } from '@/constants/zIndex';
import { type TextBoxData, TEXT_BOX_DEFAULT } from '@/overlays/textBox';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FONT_FAMILIES = [
  { label: 'Sans-serif',   value: 'sans-serif'  },
  { label: 'Serif',        value: 'serif'        },
  { label: 'Monospace',    value: 'monospace'    },
  { label: 'Arial',        value: 'Arial'        },
  { label: 'Georgia',      value: 'Georgia'      },
  { label: 'Verdana',      value: 'Verdana'      },
  { label: 'Courier New',  value: 'Courier New'  },
  { label: 'Impact',       value: 'Impact'       },
];

function ColorSwatch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="relative cursor-pointer inline-block">
      <span
        className="block w-7 h-7 rounded-lg border-2 border-border hover:border-accent transition-colors shadow-sm"
        style={{ backgroundColor: value }}
      />
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  overlayId: string;
  onClose:   () => void;
}

export function TextBoxModal({ overlayId, onClose }: Props) {
  const chart = useChartStore((s) => s.chart);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const chartExt = chart as any;

  // Read initial data from the live overlay
  const liveOverlay   = chartExt?.getOverlayById?.(overlayId) ?? null;
  const initial: TextBoxData = {
    ...TEXT_BOX_DEFAULT,
    ...(typeof liveOverlay?.extendData === 'object' && liveOverlay.extendData !== null
      ? (liveOverlay.extendData as Partial<TextBoxData>)
      : {}),
  };
  const originalDataRef = useRef<TextBoxData>(initial);

  // ── State ────────────────────────────────────────────────────────────────
  const [text,        setText]        = useState(initial.text);
  const [fontSize,    setFontSize]    = useState(initial.fontSize);
  const [fontFamily,  setFontFamily]  = useState(initial.fontFamily);
  const [bold,        setBold]        = useState(initial.bold);
  const [italic,      setItalic]      = useState(initial.italic);
  const [textColor,   setTextColor]   = useState(initial.textColor);
  const [bgColor,     setBgColor]     = useState(initial.bgColor);
  const [bgOpacity,   setBgOpacity]   = useState(initial.bgOpacity);
  const [borderColor, setBorderColor] = useState(initial.borderColor);
  const [borderWidth, setBorderWidth] = useState(initial.borderWidth);

  // Build the full data object from current state
  function snap(patch?: Partial<TextBoxData>): TextBoxData {
    return {
      text, fontSize, fontFamily, bold, italic,
      textColor, bgColor, bgOpacity, borderColor, borderWidth,
      ...patch,
    };
  }

  // Push to chart for live preview
  function push(data: TextBoxData) {
    chartExt?.overrideOverlay({ id: overlayId, extendData: data } as Partial<OverlayCreate>);
  }

  // Setters + live preview
  function chText(v: string)        { setText(v);        push(snap({ text: v }));        }
  function chFontSize(v: number)    { setFontSize(v);    push(snap({ fontSize: v }));    }
  function chFontFamily(v: string)  { setFontFamily(v);  push(snap({ fontFamily: v }));  }
  function chBold(v: boolean)       { setBold(v);        push(snap({ bold: v }));        }
  function chItalic(v: boolean)     { setItalic(v);      push(snap({ italic: v }));      }
  function chTextColor(v: string)   { setTextColor(v);   push(snap({ textColor: v }));   }
  function chBgColor(v: string)     { setBgColor(v);     push(snap({ bgColor: v }));     }
  function chBgOpacity(v: number)   { setBgOpacity(v);   push(snap({ bgOpacity: v }));   }
  function chBorderColor(v: string) { setBorderColor(v); push(snap({ borderColor: v })); }
  function chBorderWidth(v: number) { setBorderWidth(v); push(snap({ borderWidth: v })); }

  function handleCancel() {
    push(originalDataRef.current);
    onClose();
  }

  // Shared classes
  const labelCls   = 'text-sm font-semibold uppercase tracking-wider text-modal-muted mb-2';
  const rowCls     = 'flex items-center justify-between text-sm text-modal-text gap-3';
  const sectionCls = 'flex flex-col gap-2';
  const toggleCls  = (on: boolean) =>
    `px-2.5 py-1 rounded text-sm border transition-colors ${
      on
        ? 'border-accent bg-accent/20 text-accent'
        : 'border-border text-modal-muted hover:text-modal-text hover:border-accent/60'
    }`;

  return (
    <Modal onClose={handleCancel} size="sm" zIndex={Z_INDEX.topmost}>
      <Modal.Header onClose={handleCancel} title="Text Box" />

      <Modal.Body className="max-h-[72vh]">

          {/* TEXT CONTENT */}
          <div className={sectionCls}>
            <p className={labelCls}>Text</p>
            <textarea
              rows={3}
              value={text}
              onChange={(e) => chText(e.target.value)}
              placeholder="Enter text…"
              className="w-full rounded-lg border border-border bg-modal-bg text-modal-text text-sm px-3 py-2 resize-y focus:outline-none focus:border-accent transition-colors"
            />
          </div>

          {/* FONT */}
          <div className={sectionCls}>
            <p className={labelCls}>Font</p>

            {/* Family */}
            <div className={rowCls}>
              <span>Family</span>
              <select
                value={fontFamily}
                onChange={(e) => chFontFamily(e.target.value)}
                className="rounded-lg border border-border bg-modal-bg text-modal-text text-sm px-2 py-1 focus:outline-none focus:border-accent transition-colors"
              >
                {FONT_FAMILIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>

            {/* Size */}
            <div className={rowCls}>
              <span>Size&ensp;<b className="text-accent">{fontSize}px</b></span>
              <input type="range" min={8} max={72} step={1} value={fontSize}
                onChange={(e) => chFontSize(Number(e.target.value))}
                className="w-32 accent-[var(--color-accent)]" />
            </div>

            {/* Bold / Italic toggles */}
            <div className={rowCls}>
              <span>Style</span>
              <div className="flex gap-1">
                <button onClick={() => chBold(!bold)}   className={toggleCls(bold)}>Bold</button>
                <button onClick={() => chItalic(!italic)} className={toggleCls(italic)}>Italic</button>
              </div>
            </div>

            {/* Text colour */}
            <div className={rowCls}>
              <span>Text color</span>
              <ColorSwatch value={textColor} onChange={chTextColor} />
            </div>
          </div>

          {/* BACKGROUND */}
          <div className={sectionCls}>
            <p className={labelCls}>Background</p>
            <div className={rowCls}>
              <span>Color</span>
              <ColorSwatch value={bgColor} onChange={chBgColor} />
            </div>
            <div className={rowCls}>
              <span>Opacity&ensp;<b className="text-accent">{bgOpacity}%</b></span>
              <input type="range" min={0} max={100} step={5} value={bgOpacity}
                onChange={(e) => chBgOpacity(Number(e.target.value))}
                className="w-32 accent-[var(--color-accent)]" />
            </div>
          </div>

          {/* BORDER */}
          <div className={sectionCls}>
            <p className={labelCls}>Border</p>
            <div className={rowCls}>
              <span>Color</span>
              <ColorSwatch value={borderColor} onChange={chBorderColor} />
            </div>
            <div className={rowCls}>
              <span>Width&ensp;<b className="text-accent">{borderWidth}px</b></span>
              <input type="range" min={0} max={10} step={1} value={borderWidth}
                onChange={(e) => chBorderWidth(Number(e.target.value))}
                className="w-32 accent-[var(--color-accent)]" />
            </div>
          </div>
      </Modal.Body>

      <Modal.Footer>
        <button onClick={handleCancel}
          className="text-sm px-3 py-1.5 rounded-lg border border-border text-modal-muted hover:text-modal-text hover:bg-modal-hover transition-colors">
          Cancel
        </button>
        <button onClick={onClose}
          className="text-sm px-4 py-1.5 rounded-lg bg-accent text-white hover:bg-accent/80 transition-colors font-medium">
          Done
        </button>
      </Modal.Footer>
    </Modal>
  );
}
